import { useContext } from "react";
import { useInfiniteQuery } from "react-query";

import { fetchDeploymentFindingsPaginated } from "@api/findings/fetchFindings";
import { useFindingFilters } from "@hooks/useFindingFilters";
import { useTimeLookbackRange } from "@hooks/useTimeLookbackRange";
import { OrgContext } from "@providers";
import { SECOND, timeLookbackRangeToSince } from "@utils/util";

import { FindingsFilters } from "@pages/findings/types";

const NO_FINDINGS_REFETCH_INTERVAL_MS = 10 * SECOND;

interface Options {
  limit?: number;
  filterOverrides?: FindingsFilters;
}

export function useDeploymentFindings({
  limit = 10,
  filterOverrides = {},
}: Options = {}) {
  const { org } = useContext(OrgContext);
  const { tlr } = useTimeLookbackRange();
  const { findingFilters: userFindingFilters } = useFindingFilters();

  const findingFilters = { ...userFindingFilters, ...filterOverrides };

  if (!org) {
    throw new Error("No org");
  }
  const search = new URLSearchParams();

  if (findingFilters) {
    Object.entries(findingFilters).forEach(([key, value]) => {
      if (value) {
        search.set(key, String(value));
      }
    });
  }
  // Currently we're using an arbitrary limit of 10
  // TODO: Update this to read from state after we add page size setting on the data table
  search.set("limit", limit.toString());
  if (tlr) {
    search.set("since", String(timeLookbackRangeToSince(tlr)));
  }

  return useInfiniteQuery(
    ["DeploymentFindings", org, findingFilters, tlr, limit],
    ({ pageParam: cursor = "" }) => {
      return fetchDeploymentFindingsPaginated(org, search, cursor);
    },
    {
      // TODO: add getPreviousPageParam for backwards pagination (backend doesn't support this yet)
      getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
      // Without this, the api call will call the issue_counts endpoint
      // once on load, and then another time after findingsFilters are set.
      // We don’t really want the issue counts without filters applied anyways,
      // so we wait for that object to have at least one filter value (it’s initialized as {})
      enabled: !!org && Object.keys(findingFilters).length > 0,
      // if no findings were loaded, continually refresh until we get some findings.
      // This ensures that findings show up promptly for first scans.
      refetchInterval: (data) => {
        return data?.pages[0].fetchedFindings.length === 0
          ? NO_FINDINGS_REFETCH_INTERVAL_MS
          : false;
      },
    }
  );
}
